import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Collapse,
  DialogContent,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { FC, memo, ReactNode, useContext, useEffect, useId, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import LicenseContext from 'src/contexts/LicenseContext';
import { Senario, SpilloverPriority } from 'src/entities/planningEntity';
import { HowToPlanning, PlanningConditions } from 'src/entities/PlanningHistory.entity';
import { RelaxedRuleVo } from 'src/vo/RelaxedRule.vo';

import { buildPlanningConditionsSnapshot } from './buildPlanningConditionsSnapshot';
import {
  DEFAULT_PLANNING_SETTINGS,
  DISPATCH_MODEL_DEFS,
  DispatchModel,
  EXPAND_SETTING_KEYS,
  hidesConstraintUi,
  isStandardLikeModel,
  PlanningDialogSettings,
  resolveEffectiveSettings,
  SHIKI_HIDDEN_COMPANY_IDS,
  toAlgorithmSelection,
  VehicleSizePreference,
} from './dispatchModel';
import {
  PLANNING_SETTINGS_STORAGE_KEY,
  pickStandardSettings,
  restorePlanningDialog,
  STANDARD_SETTINGS_STORAGE_KEY,
} from './planningDialogStorage';
import { relaxedRuleLabel } from './relaxedRuleLabels';

type Props = {
  screenName: string;
  updateBalancedLoading: (balancedLoading: boolean) => void;
  updateExcludeToll: (bool: boolean) => void;
  updateIncludeFutureDeliveries: (bool: boolean) => void;
  updateAutoSplitOrders: (bool: boolean) => void;
  updateDontExchangePreroute: (bool: boolean) => void;
  updateConcurrentAllOrNothing: (bool: boolean) => void;
  updateExpandWorkingStartMinutes: (minutes: number) => void;
  updateExpandWorkingEndMinutes: (minutes: number) => void;
  updateExpandLoadStartMinutes: (minutes: number) => void;
  updateExpandLoadEndMinutes: (minutes: number) => void;
  updateExpandUnloadStartMinutes: (minutes: number) => void;
  updateExpandUnloadEndMinutes: (minutes: number) => void;
  updateExpandMaxVolumeRate: (rate: number) => void;
  updateExpandMaxLoadCapacityRate: (rate: number) => void;
  planningRequest: (shiftIds: number[], orderIds: number[]) => void;
  onClose: () => void;
  selectedDeliveryIds: number[];
  selectedOrderIds: number[];
  startOn: string;
  relaxedRules: RelaxedRuleVo[];
  selectedRelaxedRules: RelaxedRuleVo[];
  setSelectedRelaxedRules: (value: RelaxedRuleVo[]) => void;
  setPriorityLargeTrucks: (priority: number) => void;
  priorityLargeTrucks: number;
  updateActivateIncrementalMaxNumPistons: (bool: boolean) => void;
  updateSenario: (senario: Senario) => void;
  updateSpilloverPriority: (spilloverPriority: SpilloverPriority | undefined) => void;
  updateDisableGlobalOpt: (bool: boolean) => void;
  updateTraceCompanyId: (id: number) => void;
  /**
   * 選択済みのうち、呼び出し元のフィルタで非表示になっている件数。
   * 未指定なら警告を出さない（V3Plans / V3Timeline / TrunkTransportation は未指定のまま）。
   */
  hiddenSelectedOrderCount?: number;
  /**
   * 画面設定 (buildPlanningConditionsSnapshot の返り値) が変わるたびに呼ばれる。
   * シナリオ履歴 (planning_setting.planningConditions) を必要とするのは Map だけなので
   * optional とし、localStorage への保存は従来どおり本コンポーネント自身が行う。
   */
  onPlanningConditionsChange?: (conditions: PlanningConditions) => void;
};

const HOW_TO_PLANNING_OPTIONS = [
  { value: '最適化', label: '最適化', hint: '台数と時間距離を最小化' },
  { value: '平準化', label: '平準化', hint: '荷物を均等に割付' },
];

const VEHICLE_SIZE_OPTIONS = [
  { value: 'none', label: '指定なし' },
  { value: 'small', label: '小型' },
  { value: 'large', label: '大型' },
];

function FieldBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.75 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function SummaryChip({ label, highlight }: { label: string; highlight?: boolean }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        height: 20,
        fontSize: 10,
        fontWeight: 600,
        borderRadius: 1,
        bgcolor: highlight ? 'primary.main' : 'action.selected',
        color: highlight ? 'primary.contrastText' : 'text.secondary',
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
}

// Why not 試作どおりの Box + onClick: フォーカスも役割も無く、旧実装(Select / ToggleButtonGroup)でできた
//      キーボード・読み上げでの操作ができなくなるため。見た目は試作のまま ButtonBase と radio で組む
function Segmented({ label, value, options, onChange }: {
  label: string;
  value: string;
  options: { value: string; label: string; hint?: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <Box
      role="radiogroup"
      aria-label={label}
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${options.length}, 1fr)`,
        gap: 0,
        p: 0.375,
        borderRadius: 1.25,
        bgcolor: 'action.hover',
        border: 1,
        borderColor: 'divider',
      }}
    >
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          // How: describeChild でヒントを説明(aria-describedby)にし、選択肢の名前(aria-label)を上書きさせない
          <Tooltip key={opt.value} title={opt.hint ?? ''} placement="top" disableHoverListener={!opt.hint} describeChild>
            <ButtonBase
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(opt.value)}
              sx={{
                px: 1,
                py: 0.75,
                fontSize: 12,
                fontWeight: 600,
                textAlign: 'center',
                cursor: 'pointer',
                userSelect: 'none',
                borderRadius: 1,
                transition: 'all .15s',
                bgcolor: selected ? 'background.paper' : 'transparent',
                color: selected ? 'text.primary' : 'text.secondary',
                boxShadow: selected ? '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)' : 'none',
                '&:hover': { color: 'text.primary' },
              }}
            >
              {opt.label}
            </ButtonBase>
          </Tooltip>
        );
      })}
    </Box>
  );
}

function ToggleRow({ label, tooltip, checked, onChange }: {
  label: string;
  tooltip?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      // 右の 10px は、オンのスイッチのクリック用 input（幅 300%）が枠の右へ 32px 出るのを、
      // 本体の右余白 22.5px と合わせて受け止めるため（下限は 9.5px。これ未満だと横スクロールバーが出る）。
      // 左の 12px は、ホバーの背景の端から文字までを、右端からオンのつまみまで（10 + 2px）と揃えるため。
      // Why not 本体を overflowX: hidden / テーマの MuiSwitch を変える: 前者はほかのはみ出しも隠し、
      // 後者はアプリ中のスイッチの見た目が変わる
      sx={{
        py: 0.75,
        pl: '12px',
        pr: '10px',
        cursor: 'pointer',
        borderRadius: 1,
        '&:hover': { bgcolor: 'action.hover' },
      }}
      onClick={() => onChange(!checked)}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Typography variant="body2" sx={{ fontSize: 13 }}>{label}</Typography>
        {tooltip && (
          <Tooltip title={tooltip} placement="top">
            <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} onClick={(e) => e.stopPropagation()} />
          </Tooltip>
        )}
      </Stack>
      {/* Why not inputProps: MUI 7.3 の Switch は自前の slotProps.input(role: 'switch')を SwitchBase に渡し、
          inputProps を上書きするため aria-label が付かない(node_modules/@mui/material/Switch/Switch.js) */}
      <Switch
        size="small"
        checked={checked}
        slotProps={{ input: { 'aria-label': label } }}
        onChange={(_, v) => onChange(v)}
        onClick={(e) => e.stopPropagation()}
      />
    </Stack>
  );
}

function TinyButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        fontSize: 10,
        fontWeight: 600,
        color: 'text.secondary',
        px: 0.75,
        py: 0.25,
        borderRadius: 0.75,
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
      }}
    >
      {children}
    </ButtonBase>
  );
}

type ConstraintRowProps = {
  label: string;
  value: number;
  onChange: (v: number) => void;
  unit: string;
  max?: number;
  disabled?: boolean;
};

function ConstraintRow({ label, value, onChange, unit, max = 120, disabled = false }: ConstraintRowProps) {
  const active = value > 0;
  return (
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <Typography
        variant="caption"
        sx={{ fontSize: 11, color: active ? 'text.primary' : 'text.secondary', fontWeight: active ? 600 : 400, width: 56, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Slider
        value={value}
        onChange={(_, v) => { if (typeof v === 'number') onChange(v); }}
        min={0}
        max={max}
        step={5}
        size="small"
        disabled={disabled}
        sx={{
          flex: 1,
          '& .MuiSlider-thumb': { width: 12, height: 12 },
        }}
      />
      {/* Why not 試作の数値入力(step 未指定): 5 の倍数以外も手入力できる今の挙動を残すため step: 1 を明示する */}
      <TextField
        type="number"
        size="small"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Math.min(max, Math.max(0, Number(e.target.value) || 0)))}
        inputProps={{ min: 0, max, step: 1, style: { textAlign: 'center', padding: '3px 4px', fontSize: 11 } }}
        sx={{ width: 52 }}
      />
      <Typography variant="caption" sx={{ fontSize: 10, color: 'text.secondary', flexShrink: 0, width: 26 }}>
        {unit}
      </Typography>
    </Stack>
  );
}

const PlanningPresenter: FC<Props> = memo(
  ({
    screenName,
    updateBalancedLoading,
    updateExcludeToll,
    updateIncludeFutureDeliveries,
    updateAutoSplitOrders,
    updateDontExchangePreroute,
    updateConcurrentAllOrNothing,
    updateExpandWorkingStartMinutes,
    updateExpandWorkingEndMinutes,
    updateExpandLoadStartMinutes,
    updateExpandLoadEndMinutes,
    updateExpandUnloadStartMinutes,
    updateExpandUnloadEndMinutes,
    updateExpandMaxVolumeRate,
    updateExpandMaxLoadCapacityRate,
    planningRequest,
    onClose,
    selectedDeliveryIds,
    selectedOrderIds,
    relaxedRules,
    selectedRelaxedRules,
    setSelectedRelaxedRules,
    setPriorityLargeTrucks,
    priorityLargeTrucks,
    updateActivateIncrementalMaxNumPistons,
    updateSenario,
    updateSpilloverPriority,
    updateDisableGlobalOpt,
    updateTraceCompanyId,
    hiddenSelectedOrderCount,
    onPlanningConditionsChange,
  }) => {
    const licenseContext = useContext(LicenseContext);

    const [settings, setSettings] = useState<PlanningDialogSettings>(DEFAULT_PLANNING_SETTINGS);
    const updateSetting = <K extends keyof PlanningDialogSettings>(key: K, value: PlanningDialogSettings[K]) =>
      setSettings((prev) => ({ ...prev, [key]: value }));
    // How: 復元が終わるまで保存しない。復元の setState は同じコミットの保存 effect に反映されないので、
    //      止めないと「初期値を書いてから復元値を書き直す」1 往復が起き、その間に別画面が既定値を読みうる
    const [isRestored, setIsRestored] = useState(false);
    const [isExecuting, setIsExecuting] = useState<boolean>(false);

    const useAiPlanning = licenseContext.config?.use_ai_planning === true;
    // 学習配車で最新シナリオ apprentice_v2 を送る会社か(#410)。API が選択会社基準で返す
    const useApprenticeV2 = licenseContext.config?.use_apprentice_v2 === true;

    // How: 選べるモデルを環境とライセンスで決める。optfleet は検証用なので stg / dev だけに出す(#404)。
    //      AI配車ライセンスは判定しない(STG ではライセンスの無い会社でも検証できるようにするため)。
    // Why not app_env !== 'prd' の denylist にしない: config 取得前は app_env が undefined なので
    //      denylist だと prd でも config が届くまでのあいだ選択肢に入る。V2Plans の PlanningPresenter と
    //      API の CreateInputAndConfigJob#not_prd? も同じ allowlist。
    const canUseOptfleet = ['stg', 'dev'].includes(licenseContext.config?.app_env ?? '');
    // Why 事業所とログイン会社の両方を見る: 事業所を「すべて表示する」にすると selected_company_id が 0 になり、
    //      事業所だけで判定すると SHIKI が戻る（SwitchCompanies / V2ShipmentOrderForm と同じ読み方）
    const currentCompanyId = licenseContext.config?.selected_company_id || licenseContext.config?.company_id;
    const canUseShiki = !SHIKI_HIDDEN_COMPANY_IDS.includes(currentCompanyId ?? 0);
    const availableModels: DispatchModel[] = useMemo(
      () => DISPATCH_MODEL_DEFS
        .filter(({ value }) => {
          if (value === 'optfleet') return canUseOptfleet;
          if (value === 'standard') return true;
          if (value === 'shiki') return useAiPlanning && canUseShiki;
          return useAiPlanning;
        })
        .map(({ value }) => value),
      [canUseOptfleet, canUseShiki, useAiPlanning],
    );

    // How: 送る値の導出を一本化する。送信 effect・保存・画面表示が別々に導出すると食い違うため。
    // Why not: 選択肢に出していないモデルは標準として扱う。prd に残った optfleet や、ライセンスを
    //      失った会社の SHIKI が、本人に見えないまま送られるのを防ぐ。
    const effectiveModel: DispatchModel = availableModels.includes(settings.dispatchModel)
      ? settings.dispatchModel
      : 'standard';
    // 制約 UI を出さないモデルか(SAHAI 常時 / v2 の学習配車)。タブの出し分け・送る値・state の
    // リセットがこの 1 つを見る
    const hidesConstraints = hidesConstraintUi(effectiveModel, useApprenticeV2);
    const effective = useMemo(
      () => resolveEffectiveSettings(effectiveModel, settings, selectedRelaxedRules, useApprenticeV2),
      [effectiveModel, settings, selectedRelaxedRules, useApprenticeV2],
    );

    const activeConstraintCount = selectedRelaxedRules.length
      + EXPAND_SETTING_KEYS.filter((key) => settings[key] > 0).length;
    const hasConstraintValues = activeConstraintCount > 0;
    const hasExpandValues = EXPAND_SETTING_KEYS.some((key) => settings[key] > 0);

    const [tab, setTab] = useState(0);
    const [advancedOpen, setAdvancedOpen] = useState(hasConstraintValues);
    const expandRegionId = useId();

    // How: effectiveModel を senario / spillover_priority / trace_company_id / disable_global_opt へ(#286, #366)。
    // Why not: localStorage 復元側をライセンスでガードしない。config 取得前の初回レンダーでは useAiPlanning が
    //          false になり、復元を止めると直後の保存が既定値で上書きしてライセンス保有者の選択まで消える(#292)。
    useEffect(() => {
      const selection = toAlgorithmSelection(effectiveModel, licenseContext.config?.selected_company_id ?? 0);
      updateSenario(selection.senario);
      updateSpilloverPriority(selection.spilloverPriority);
      updateTraceCompanyId(selection.traceCompanyId);
      updateDisableGlobalOpt(selection.disableGlobalOpt);
    }, [effectiveModel, licenseContext.config?.selected_company_id, updateSenario, updateSpilloverPriority, updateTraceCompanyId, updateDisableGlobalOpt]);

    // How: 制約 UI を出さないモデルでは、無効にする制約（親の state）と緩和値をリセットする
    //      (#366。#296 の「数量」ガードの置き換え。v2 の学習配車も同じ扱いにする #410)。
    //      無効にする制約は画面によってリクエスト時に親の state から読まれるため、送る値の導出だけでは足りない。
    // Why not モデルが変わったときに 1 回だけリセットする: ライセンス取得と復元が同じコミットで重なると、
    //      復元が戻した値が残る。値が残っている間は毎回外す。
    // Why not config を見ずに settings.dispatchModel で判定する: config 取得前に SAHAI の保存値で
    //      リセットすると、ライセンスが無い会社の制約まで消える(#292 と同じ罠)。v2 かどうかも同じ
    //      config から来るので、config が届く前は effectiveModel が standard に倒れてリセットしない。
    useEffect(() => {
      if (!hidesConstraints) return;
      if (selectedRelaxedRules.length > 0) setSelectedRelaxedRules([]);
      if (EXPAND_SETTING_KEYS.some((key) => settings[key] !== 0)) {
        setSettings((prev) => ({ ...prev, ...Object.fromEntries(EXPAND_SETTING_KEYS.map((key) => [key, 0])) }));
      }
    }, [hidesConstraints, selectedRelaxedRules, settings, setSelectedRelaxedRules]);

    useEffect(() => {
      updateBalancedLoading(effective.settings.howToPlanning === '平準化');
    }, [effective.settings.howToPlanning, updateBalancedLoading]);

    // vehicleSizePreference → priorityLargeTrucks マッピング
    useEffect(() => {
      const preference = effective.settings.vehicleSizePreference;
      if (preference === 'none') setPriorityLargeTrucks(0.5);
      else if (preference === 'small') setPriorityLargeTrucks(0);
      else setPriorityLargeTrucks(1.0);
    }, [effective.settings.vehicleSizePreference, setPriorityLargeTrucks]);

    useEffect(() => {
      updateExcludeToll(!effective.settings.availabilityOfExpressway);
    }, [effective.settings.availabilityOfExpressway, updateExcludeToll]);

    useEffect(() => {
      updateIncludeFutureDeliveries(effective.settings.includeFutureDeliveries);
    }, [effective.settings.includeFutureDeliveries, updateIncludeFutureDeliveries]);

    useEffect(() => {
      updateAutoSplitOrders(effective.settings.autoSplitOrders);
    }, [effective.settings.autoSplitOrders, updateAutoSplitOrders]);

    useEffect(() => {
      updateDontExchangePreroute(effective.settings.dontExchangePreroute);
    }, [effective.settings.dontExchangePreroute, updateDontExchangePreroute]);

    useEffect(() => {
      updateConcurrentAllOrNothing(effective.settings.concurrentAllOrNothing);
    }, [effective.settings.concurrentAllOrNothing, updateConcurrentAllOrNothing]);

    useEffect(() => {
      updateActivateIncrementalMaxNumPistons(effective.settings.activateIncrementalMaxNumPistons);
    }, [effective.settings.activateIncrementalMaxNumPistons, updateActivateIncrementalMaxNumPistons]);

    useEffect(() => {
      updateExpandWorkingStartMinutes(effective.settings.expandWorkingTimeStartMinutes);
    }, [effective.settings.expandWorkingTimeStartMinutes, updateExpandWorkingStartMinutes]);

    useEffect(() => {
      updateExpandWorkingEndMinutes(effective.settings.expandWorkingTimeEndMinutes);
    }, [effective.settings.expandWorkingTimeEndMinutes, updateExpandWorkingEndMinutes]);

    useEffect(() => {
      updateExpandLoadStartMinutes(effective.settings.expandLoadStartMinutes);
    }, [effective.settings.expandLoadStartMinutes, updateExpandLoadStartMinutes]);

    useEffect(() => {
      updateExpandLoadEndMinutes(effective.settings.expandLoadEndMinutes);
    }, [effective.settings.expandLoadEndMinutes, updateExpandLoadEndMinutes]);

    useEffect(() => {
      updateExpandUnloadStartMinutes(effective.settings.expandUnloadStartMinutes);
    }, [effective.settings.expandUnloadStartMinutes, updateExpandUnloadStartMinutes]);

    useEffect(() => {
      updateExpandUnloadEndMinutes(effective.settings.expandUnloadEndMinutes);
    }, [effective.settings.expandUnloadEndMinutes, updateExpandUnloadEndMinutes]);

    useEffect(() => {
      updateExpandMaxVolumeRate(effective.settings.expandMaxVolumeRate);
    }, [effective.settings.expandMaxVolumeRate, updateExpandMaxVolumeRate]);

    useEffect(() => {
      updateExpandMaxLoadCapacityRate(effective.settings.expandMaxLoadCapacityRate);
    }, [effective.settings.expandMaxLoadCapacityRate, updateExpandMaxLoadCapacityRate]);

    // 制約選択時に緩和値をリセット
    useEffect(() => {
      if (!selectedRelaxedRules?.length) return;
      if (selectedRelaxedRules.includes('労働時間')) {
        setSettings((prev) => ({ ...prev, expandWorkingTimeStartMinutes: 0, expandWorkingTimeEndMinutes: 0 }));
      }
      if (selectedRelaxedRules.includes('指定時間')) {
        setSettings((prev) => ({
          ...prev,
          expandLoadStartMinutes: 0,
          expandLoadEndMinutes: 0,
          expandUnloadStartMinutes: 0,
          expandUnloadEndMinutes: 0,
        }));
      }
      if (selectedRelaxedRules.includes('重量')) {
        setSettings((prev) => ({ ...prev, expandMaxLoadCapacityRate: 0 }));
      }
      if (selectedRelaxedRules.includes('体積')) {
        setSettings((prev) => ({ ...prev, expandMaxVolumeRate: 0 }));
      }
    }, [selectedRelaxedRules]);

    // localStorage 復元。旧値の読み替えと 2 キーの合成は restorePlanningDialog が行う(#366)
    // Why not ライセンスでガードしない: #292（上の送信 effect のコメント参照）
    // How: config が変わるたびに読み直す（ライセンス取得後に選んだモデルを反映するため）
    useEffect(() => {
      const restored = restorePlanningDialog(
        localStorage.getItem(PLANNING_SETTINGS_STORAGE_KEY),
        localStorage.getItem(STANDARD_SETTINGS_STORAGE_KEY),
      );
      setSettings(restored.settings);
      if (restored.selectedRelaxedRules) setSelectedRelaxedRules(restored.selectedRelaxedRules);
      setIsRestored(true);
    }, [licenseContext.config, setSelectedRelaxedRules]);

    // localStorage 保存 + 履歴 snapshot (onPlanningConditionsChange) への通知
    // How: 既存キーと通知には送る値を、新しいキーには画面で選んだモデルと LOG 3.0 の値を書く。
    //      既存キーは自動配車の履歴(planningConditions)の元データなので、送っていない値を記録しない。
    useEffect(() => {
      if (!isRestored) return;
      const snapshot = buildPlanningConditionsSnapshot({
        ...effective.settings,
        selectedRelaxedRules: effective.selectedRelaxedRules,
      });
      localStorage.setItem(PLANNING_SETTINGS_STORAGE_KEY, JSON.stringify(snapshot));
      localStorage.setItem(STANDARD_SETTINGS_STORAGE_KEY, JSON.stringify(pickStandardSettings(settings)));
      onPlanningConditionsChange?.(snapshot);
    }, [isRestored, effective, settings, onPlanningConditionsChange]);

    const onClick = () => {
      if (isExecuting) return;
      setIsExecuting(true);
      ReactGA.event('executePlanning', { screen_name: screenName, button_name: '自動配車' });
      onClose();
      planningRequest(selectedDeliveryIds, selectedOrderIds);
    };

    const handleChipToggle = (rule: RelaxedRuleVo) => {
      if (selectedRelaxedRules.includes(rule)) {
        setSelectedRelaxedRules(selectedRelaxedRules.filter((r) => r !== rule));
      } else {
        setSelectedRelaxedRules([...selectedRelaxedRules, rule]);
      }
    };

    const resetAll = () => {
      setSettings(DEFAULT_PLANNING_SETTINGS);
      setSelectedRelaxedRules([]);
      setTab(0);
    };

    const isStandardModel = isStandardLikeModel(effectiveModel);
    const modelLabel = DISPATCH_MODEL_DEFS.find((m) => m.value === effectiveModel)?.label ?? '';

    const constraintRows: { label: string; key: (typeof EXPAND_SETTING_KEYS)[number]; unit: string; max?: number; disabledBy: RelaxedRuleVo }[] = [
      { label: '稼働開始', key: 'expandWorkingTimeStartMinutes', unit: '分前', disabledBy: '労働時間' },
      { label: '稼働終了', key: 'expandWorkingTimeEndMinutes', unit: '分後', disabledBy: '労働時間' },
      { label: '集荷開始', key: 'expandLoadStartMinutes', unit: '分前', disabledBy: '指定時間' },
      { label: '集荷締切', key: 'expandLoadEndMinutes', unit: '分後', disabledBy: '指定時間' },
      { label: '納品開始', key: 'expandUnloadStartMinutes', unit: '分前', disabledBy: '指定時間' },
      { label: '納品締切', key: 'expandUnloadEndMinutes', unit: '分後', disabledBy: '指定時間' },
      { label: '積載重量', key: 'expandMaxLoadCapacityRate', unit: '%', max: 100, disabledBy: '重量' },
      { label: '積載体積', key: 'expandMaxVolumeRate', unit: '%', max: 100, disabledBy: '体積' },
    ];

    return (
      <>
        {/* Header */}
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5, position: 'relative' }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                自動配車
              </Typography>
              <Typography variant="caption" color="text.secondary">
                条件を設定して実行
              </Typography>
            </Box>
            <IconButton size="small" aria-label="閉じる" onClick={onClose}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>

          {/* Summary chips */}
          <Stack direction="row" spacing={0.5} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }} data-testid="planning-dialog-summary">
            <SummaryChip label={modelLabel} />
            {isStandardModel && <SummaryChip label={settings.howToPlanning} />}
            {!hidesConstraints && activeConstraintCount > 0 && (
              <SummaryChip label={`制約 ${activeConstraintCount}`} highlight />
            )}
          </Stack>
        </Box>

        {!hidesConstraints && (
          <Tabs
            value={tab}
            onChange={(_, v: number) => setTab(v)}
            sx={{
              minHeight: 36,
              px: 2.5,
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: 36,
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 600,
                px: 1.5,
              },
            }}
          >
            <Tab label="基本" />
            <Tab label="詳細制約" />
          </Tabs>
        )}

        <DialogContent sx={{ px: 2.5, py: 2, maxHeight: '58vh' }}>
          {(hiddenSelectedOrderCount ?? 0) > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              選択中のうち {hiddenSelectedOrderCount} 件は現在のフィルタで非表示です。非表示の案件も配車対象に含まれます。
            </Alert>
          )}

          {(tab === 0 || hidesConstraints) && (
            <Stack spacing={2.25}>
              {availableModels.length > 1 && (
                <FieldBlock label="モデル">
                  <FormControl fullWidth size="small">
                    <Select
                      // How: 選択肢に無いモデルが保存されていても、実際に送るモデルを表示する
                      value={effectiveModel}
                      onChange={(e: SelectChangeEvent) => {
                        updateSetting('dispatchModel', e.target.value as DispatchModel);
                        setTab(0);
                      }}
                      renderValue={(value) => (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {DISPATCH_MODEL_DEFS.find((m) => m.value === value)?.label}
                        </Typography>
                      )}
                    >
                      {DISPATCH_MODEL_DEFS.filter((model) => availableModels.includes(model.value)).map((model) => (
                        <MenuItem key={model.value} value={model.value}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{model.label}</Typography>
                            <Typography variant="caption" color="text.secondary">{model.description}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FieldBlock>
              )}

              {isStandardModel && (
                <>
                  <FieldBlock label="計算方法">
                    <Segmented
                      label="計算方法"
                      value={settings.howToPlanning}
                      options={HOW_TO_PLANNING_OPTIONS}
                      onChange={(v) => updateSetting('howToPlanning', v as HowToPlanning)}
                    />
                  </FieldBlock>

                  <FieldBlock label="車両サイズ優先">
                    <Segmented
                      label="車両サイズ優先"
                      value={settings.vehicleSizePreference}
                      options={VEHICLE_SIZE_OPTIONS}
                      onChange={(v) => updateSetting('vehicleSizePreference', v as VehicleSizePreference)}
                    />
                  </FieldBlock>
                </>
              )}

              <Divider />

              <Stack spacing={0.25}>
                <ToggleRow
                  label="有料道路を使用"
                  checked={settings.availabilityOfExpressway}
                  onChange={(v) => updateSetting('availabilityOfExpressway', v)}
                />
                {isStandardModel && (
                  <>
                    <ToggleRow
                      label="宵積みを有効"
                      checked={settings.includeFutureDeliveries}
                      onChange={(v) => updateSetting('includeFutureDeliveries', v)}
                    />
                    <ToggleRow
                      label="既存割り当てを維持"
                      tooltip="ONで現在の割り当てを維持し追加のみ。OFFで再構成"
                      checked={settings.dontExchangePreroute}
                      onChange={(v) => updateSetting('dontExchangePreroute', v)}
                    />
                    <ToggleRow
                      label="回転数を平準化"
                      checked={settings.activateIncrementalMaxNumPistons}
                      onChange={(v) => updateSetting('activateIncrementalMaxNumPistons', v)}
                    />
                    <ToggleRow
                      label="超過時に案件を自動分割"
                      checked={settings.autoSplitOrders}
                      onChange={(v) => updateSetting('autoSplitOrders', v)}
                    />
                    <ToggleRow
                      label="同一地点で複数台になる場合は割当てない"
                      checked={settings.concurrentAllOrNothing}
                      onChange={(v) => updateSetting('concurrentAllOrNothing', v)}
                    />
                  </>
                )}
              </Stack>
            </Stack>
          )}

          {tab === 1 && !hidesConstraints && (
            <Stack spacing={2.25}>
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    無効にする制約
                    {selectedRelaxedRules.length > 0 && (
                      <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'primary.main', fontWeight: 700 }}>
                        · {selectedRelaxedRules.length}
                      </Typography>
                    )}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <TinyButton onClick={() => setSelectedRelaxedRules([...relaxedRules])}>全選択</TinyButton>
                    <TinyButton onClick={() => setSelectedRelaxedRules([])}>解除</TinyButton>
                  </Stack>
                </Stack>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }} data-testid="relaxed-rule-chips">
                  {relaxedRules.map((rule) => {
                    const selected = selectedRelaxedRules.includes(rule);
                    return (
                      <Chip
                        key={rule}
                        label={relaxedRuleLabel(rule)}
                        size="small"
                        onClick={() => handleChipToggle(rule)}
                        sx={{
                          height: 'auto',
                          px: 1,
                          py: 0.375,
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 500,
                          border: 1,
                          transition: 'all .12s',
                          borderColor: selected ? 'primary.main' : 'divider',
                          bgcolor: selected ? 'primary.main' : 'transparent',
                          color: selected ? 'primary.contrastText' : 'text.secondary',
                          '& .MuiChip-label': { px: 0 },
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: selected ? 'primary.main' : 'transparent',
                            color: selected ? 'primary.contrastText' : 'primary.main',
                          },
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>

              <Divider />

              <Box>
                <ButtonBase
                  aria-expanded={advancedOpen}
                  aria-controls={expandRegionId}
                  onClick={() => setAdvancedOpen((o) => !o)}
                  sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', userSelect: 'none', borderRadius: 1 }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    制約緩和
                    {hasExpandValues && (
                      <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'primary.main', fontWeight: 700 }}>
                        · 適用中
                      </Typography>
                    )}
                  </Typography>
                  <KeyboardArrowDownIcon
                    sx={{ fontSize: 16, color: 'text.secondary', transform: advancedOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
                  />
                </ButtonBase>
                <Collapse in={advancedOpen} id={expandRegionId}>
                  <Box sx={{ pt: 1.5 }}>
                    <Stack spacing={1}>
                      {constraintRows.map((row) => (
                        <ConstraintRow
                          key={row.key}
                          label={row.label}
                          value={settings[row.key]}
                          onChange={(v) => updateSetting(row.key, v)}
                          unit={row.unit}
                          max={row.max}
                          disabled={selectedRelaxedRules.includes(row.disabledBy)}
                        />
                      ))}
                    </Stack>
                  </Box>
                </Collapse>
              </Box>
            </Stack>
          )}
        </DialogContent>

        {/* Footer */}
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Button size="small" onClick={resetAll} sx={{ color: 'text.secondary', fontSize: 12 }}>
            初期化
          </Button>
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
              キャンセル
            </Button>
            {/* Why not 試作の linear-gradient(hsl(var(--primary))): このリポジトリには CSS 変数 --primary が無い */}
            <Button
              size="small"
              variant="contained"
              disableElevation
              startIcon={<PlayArrowIcon sx={{ fontSize: 16 }} />}
              onClick={onClick}
              disabled={isExecuting}
              sx={{ px: 2.5, fontWeight: 700, borderRadius: 1.5 }}
            >
              実行する
            </Button>
          </Stack>
        </Box>
      </>
    );
  }
);

export default PlanningPresenter;
